export const MIN_PASSWORD_LENGTH = 11;
export const DUMMY_UUID = '00000000-0000-0000-0000-000000000000';

// The following logic is copied from 'uuid' source code: https://github.com/uuidjs/uuid
export function validateUUID(uuid: unknown) {
  return (
    typeof uuid === 'string' &&
    /^(?:[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}|00000000-0000-0000-0000-000000000000)$/i.test(
      uuid,
    )
  );
}

// From the HTML5 spec: https://html.spec.whatwg.org/multipage/input.html#valid-e-mail-address
export function validateEmail(email: unknown) {
  return (
    typeof email === 'string' &&
    /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/.test(
      email,
    )
  );
}

export function validatePhone(phone: unknown) {
  return typeof phone === 'string' && /^[0-9]{3}-[0-9]{3}-[0-9]{4}$/.test(phone);
}

export function generateSecurePassword(length: number) {
  function shuffleStringSecure(input: string): string {
    const arr = Array.from(input); // preserves Unicode code points
    const randInt = (maxExclusive: number): number => {
      const u32 = new Uint32Array(1);
      const bucket = Math.floor(0x100000000 / maxExclusive) * maxExclusive;
      let r: number;
      do {
        crypto.getRandomValues(u32);
        r = u32[0]!;
      } while (r >= bucket);
      return r % maxExclusive;
    };

    for (let i = arr.length - 1; i > 0; i--) {
      const j = randInt(i + 1);
      [arr[i], arr[j]] = [arr[j], arr[i]];
    }
    return arr.join('');
  }
  const MIN_UNIQUE_CHAR = 3;
  const fullCharset = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  const upper = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
  const lower = 'abcdefghijklmnopqrstuvwxyz';
  const numeric = '0123456789';

  let password = '';

  //Get random values that will represent the character selected
  const charsetValues = new Uint32Array(length);
  crypto.getRandomValues(charsetValues);
  const upperValue = new Uint32Array(1);
  crypto.getRandomValues(upperValue);
  const lowerValue = new Uint32Array(1);
  crypto.getRandomValues(lowerValue);
  const numericValue = new Uint32Array(1);
  crypto.getRandomValues(numericValue);

  password += upper[upperValue[0] % upperValue.length];
  password += lower[lowerValue[0] % lowerValue.length];
  password += numeric[numericValue[0] % numericValue.length];

  for (let i = 0; i < length - MIN_UNIQUE_CHAR; i++) {
    password += fullCharset[charsetValues[i] % fullCharset.length];
  }

  password = shuffleStringSecure(password);

  if (!validatePassword(password)) {
    throw new Error(`Validation method 'generateSecurePassword' value did not meet valid password requirements.`);
  }

  return password;
}

export function validatePassword(password: string) {
  // Regular expression for the password validation
  //Rules are at least 1 upper, 1 lower, 1 numeric and minimum password length variable
  const regexPattern = `^(?=.*[a-z])(?=.*[A-Z])(?=.*\\d)[A-Za-z\\d]{${MIN_PASSWORD_LENGTH},}$`;
  const regex = new RegExp(regexPattern);
  return regex.test(password);
}

// From Mozilla spec: https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Date/toISOString
export function validateISOTimestamp(timestamp: unknown) {
  return (
    typeof timestamp === 'string' && /^\d{4}-\d\d-\d\dT\d\d:\d\d:\d\d(\.\d+)?(([+-]\d\d:\d\d)|Z)?$/i.test(timestamp)
  );
}

export function validateUrl(url: unknown) {
  if (typeof url !== 'string') {
    return false;
  }
  try {
    // eslint-disable-next-line no-new
    new URL(url);
    return true;
  } catch {
    return false;
  }
}

export function validateVIN(vin: string) {
  const vinRegex = /^[A-HJ-NPR-Z0-9]{17}$/;
  return typeof vin === 'string' && vinRegex.test(vin);
}

export function validateZipCode(zipCode: string) {
  const zipCodeRegex = /^\d{5}(-\d{4})?$/;
  return typeof zipCode === 'string' && zipCodeRegex.test(zipCode);
}
